import React, { createContext, useState, useContext, ReactNode, useCallback, useEffect } from 'react';
import { Notification, AdminContextType, UserProfile, ReportedUser, Timestamp, AIReport, GroupGame, ReportedGroup } from './types';
import { useAuth, playSound } from './AuthContext';
import { db, Timestamp as TimestampValue, serverTimestamp, increment } from './firebase';
import { sendDirectNotification } from './notificationUtils';

export const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const useAdmin = () => {
    const context = useContext(AdminContext);
    if (!context) {
        throw new Error('useAdmin must be used within an AdminProvider');
    }
    return context;
};

interface AdminProviderProps {
    children: ReactNode;
}

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
    const { userProfile, toggleFollow } = useAuth();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [sentRequests, setSentRequests] = useState<Set<string>>(new Set());
    const [reportedUsers, setReportedUsers] = useState<ReportedUser[]>([]);
    const [aiReports, setAIReports] = useState<AIReport[]>([]);


    useEffect(() => {
        if (!userProfile || !db || userProfile.inAppNotificationsEnabled === false) {
            setNotifications([]);
            if (!userProfile || !db) setAIReports([]);
            return;
        }

        const unsubscribe = db.collection('users')
            .doc(userProfile.uid)
            .collection('notifications')
            .orderBy('time', 'desc')
            .limit(50)
            .onSnapshot((snapshot: any) => {
                const loadedNotifications = snapshot.docs.map((doc: any) => ({
                    id: doc.id,
                    ...doc.data()
                })) as Notification[];
                
                setNotifications(loadedNotifications);
            }, (error: any) => {
                console.error("Error listening to notifications:", error);
            });
        
        let unsubscribeAIReports = () => {};

        if (userProfile.isAdmin) {
            unsubscribeAIReports = db.collection('ai_reports')
                .orderBy('reportedAt', 'desc')
                .onSnapshot((snapshot: any) => {
                    const reports = snapshot.docs.map((doc: any) => ({
                        reportId: doc.id,
                        ...doc.data()
                    })) as AIReport[];
                    setAIReports(reports);
                }, (error: any) => {
                    console.error("Error fetching AI reports:", error);
                });
        }

        return () => {
            unsubscribe();
            unsubscribeAIReports();
        }
    }, [userProfile]);
    
    const addNotification = async (targetUserId: string, notificationData: Omit<Notification, 'id' | 'time' | 'read'>) => {
        if (!targetUserId || !db) return;
        
        try {
            const userDoc = await db.collection('users').doc(targetUserId).get();
            if (userDoc.exists) {
                const userData = userDoc.data();
                if (userData && userData.inAppNotificationsEnabled === false) {
                    return; // User has disabled in-app notifications
                }
            }

            await db.collection('users')
                .doc(targetUserId)
                .collection('notifications')
                .add({
                    ...notificationData,
                    time: TimestampValue.now(),
                    read: false,
                });
        } catch (error) {
            console.error(`Error adding notification to ${targetUserId}:`, error);
        }
    };

    const sendFriendRequest = (targetUserId: string) => {
        if (!userProfile || sentRequests.has(targetUserId) || userProfile.following.includes(targetUserId)) {
            return;
        }
        
        setSentRequests(prev => new Set(prev).add(targetUserId));
        
        toggleFollow(targetUserId);

        sendDirectNotification(targetUserId, {
            type: 'friend_request',
            text: `sent you a friend request.`,
            fromUser: {
                id: userProfile.uid,
                name: userProfile.displayName,
                avatar: userProfile.photoURL,
            }
        });
    };

    const markAllNotificationsAsRead = async () => {
        if (!userProfile) return;

        // We now mark ALL notifications as read, including requests.
        // Requests will still appear in the "Requests" list because that list
        // filters by type, not by read status.
        const unreadNotifs = notifications.filter(n => !n.read);
        
        console.log(`Marking ${unreadNotifs.length} notifications as read.`);

        if (unreadNotifs.length === 0) return;

        // Optimistic update to make UI feel instant
        setNotifications(prev => prev.map(n => {
            if (!n.read) {
                return { ...n, read: true };
            }
            return n;
        }));

        try {
            const userNotifsRef = db.collection('users').doc(userProfile.uid).collection('notifications');
            
            // Firestore batch limit is 500 operations.
            // We'll process in chunks of 450 to be safe.
            const chunkSize = 450;
            for (let i = 0; i < unreadNotifs.length; i += chunkSize) {
                const chunk = unreadNotifs.slice(i, i + chunkSize);
                const batch = db.batch();
                
                chunk.forEach(notif => {
                    const docRef = userNotifsRef.doc(notif.id);
                    batch.update(docRef, { read: true });
                });
                
                await batch.commit();
                console.log(`Committed batch of ${chunk.length} updates.`);
            }

            console.log("Successfully marked all notifications as read in database.");
        } catch (error) {
            console.error("Error marking all notifications as read:", error);
            // Note: Snapshot listener will likely correct the state if the write failed,
            // but we could revert the optimistic update here if needed.
        }
    };

    const markSingleNotificationAsRead = async (notificationId: string) => {
        if (!userProfile) return;

        try {
            const notifRef = db.collection('users').doc(userProfile.uid).collection('notifications').doc(notificationId);
            await notifRef.update({ read: true });
        } catch (error) {
            console.error("Error marking single notification as read:", error);
        }
    };

    const removeNotification = async (notificationId: string) => {
        if (!userProfile) return;
        try {
            await db.collection('users')
                .doc(userProfile.uid)
                .collection('notifications')
                .doc(notificationId)
                .delete();
        } catch (error) {
            console.error("Error removing notification:", error);
        }
    };

    const refreshNotifications = useCallback(async () => {
    }, []);

    const reportUser = (userToReport: UserProfile, reason: string, details: string) => {
        const newReport: Omit<ReportedUser, 'reportId'> = {
            user: userToReport,
            reason,
            details,
            reportedAt: TimestampValue.now(),
        };
        console.log("Reporting user:", newReport);
    };
    
    const reportGroup = async (group: GroupGame, reason: string, details: string) => {
        const newReport: Omit<ReportedGroup, 'reportId'> = {
            groupId: group.id,
            groupName: group.name,
            creatorId: group.creatorId,
            reason,
            details,
            reportedAt: TimestampValue.now(),
        };
        
        try {
            await db.collection('reported_groups').add(newReport);
        } catch(error) {
            console.error("Error reporting group:", error);
        }
    };

    const submitAIReport = async (report: Omit<AIReport, 'reportId' | 'status'>) => {
        const newReport = {
            ...report,
            status: 'new' as 'new',
            reportedAt: serverTimestamp(),
        };
        try {
            await db.collection('ai_reports').add(newReport);
        } catch (error) {
            console.error("Error submitting AI report:", error);
            throw error;
        }
    };

    const dismissUserReport = (reportId: string) => {
        setReportedUsers(prev => prev.filter(r => r.reportId !== reportId));
    };

    const resolveAIReport = async (reportId: string) => {
        try {
            await db.collection('ai_reports').doc(reportId).update({ status: 'resolved' });
        } catch (error) {
            console.error("Error resolving AI report:", error);
        }
    };

    const banUser = async (uid: string) => {
        try {
            await db.collection('users').doc(uid).update({ isBanned: true });
        } catch (error) {
            console.error("Failed to ban user:", error);
        }
    };

    const unbanUser = async (uid: string) => {
        try {
            await db.collection('users').doc(uid).update({ isBanned: false });
        } catch (error) {
            console.error("Failed to unban user:", error);
        }
    };

    const deleteUser = async (uid: string) => {
        try {
            await db.collection('users').doc(uid).delete();
        } catch (error) {
            console.error("Failed to delete user:", error);
        }
    };

    const banAndWipeUser = async (targetUser: UserProfile, reason?: string) => {
        if (!db) return;
        try {
            const batch = db.batch();
            
            // 1. Add to banned_list
            if (targetUser.email) {
                const banRef = db.collection('banned_list').doc(targetUser.email.toLowerCase());
                batch.set(banRef, {
                    identifier: targetUser.email.toLowerCase(),
                    type: 'email',
                    bannedAt: serverTimestamp(),
                    reason: reason || 'Violation of community guidelines'
                });
            }
            
            if (targetUser.username) {
                const usernameBanRef = db.collection('banned_list').doc(`u_${targetUser.username.toLowerCase()}`);
                batch.set(usernameBanRef, {
                    identifier: targetUser.username.toLowerCase(),
                    type: 'username',
                    bannedAt: serverTimestamp(),
                    reason: reason || 'Violation of community guidelines'
                });
            }

            // 2. Delete User Profile
            batch.delete(db.collection('users').doc(targetUser.uid));
            
            // 3. Delete Username mapping
            if (targetUser.username) {
                batch.delete(db.collection('usernames').doc(targetUser.username));
            }

            // 4. Wipe Posts (Puzzles) - Limit to first 100 for safety in batch
            const puzzlesQuery = await db.collection('puzzles').where('creatorId', '==', targetUser.uid).limit(100).get();
            puzzlesQuery.docs.forEach((doc: any) => {
                batch.delete(db.collection('puzzles').doc(doc.id));
            });

            await batch.commit();
            console.log(`User ${targetUser.displayName} banned and wiped.`);
        } catch (error) {
            console.error("Failed to ban and wipe user:", error);
            throw error;
        }
    };

    const resetLeaderboard = async () => {
        try {
            const usersSnapshot = await db.collection('users').get();
            const batch = db.batch();
    
            usersSnapshot.docs.forEach((doc: any) => {
                const userRef = db.collection('users').doc(doc.id);
                batch.update(userRef, { score: 0 });
            });
    
            await batch.commit();
        } catch (error) {
            console.error("Error resetting leaderboard:", error);
        }
    };
    
    const sendBroadcastMessage = async () => {
        try {
            const adminQuery = await db.collection('users').where('isAdmin', '==', true).limit(1).get();
            if (adminQuery.empty) {
                throw new Error("Admin user not found.");
            }
            const adminUser = adminQuery.docs[0].data() as UserProfile;
    
            const broadcastMessage = {
                senderId: adminUser.uid,
                text: "Hello everyone! This is a friendly reminder about our community guidelines. Please be respectful and keep our space safe and fun. Any violations of our privacy policy or terms of service will result in immediate action. Let's continue to make this a great place for puzzles and friends! ✨",
                createdAt: TimestampValue.now(),
                read: false,
            };
    
            const usersSnapshot = await db.collection('users').where('isAdmin', '!=', true).get();
            const batch = db.batch();
    
            for (const userDoc of usersSnapshot.docs) {
                const user = userDoc.data() as UserProfile;
                const participants = [adminUser.uid, user.uid].sort();
    
                const convoQuery = await db.collection('conversations')
                    .where('participants', '==', participants)
                    .limit(1).get();
                
                let convoId: string;
                let convoRef;
                if (convoQuery.empty) {
                    convoRef = db.collection('conversations').doc();
                    batch.set(convoRef, { participants });
                    convoId = convoRef.id;
                } else {
                    convoId = convoQuery.docs[0].id;
                    convoRef = db.collection('conversations').doc(convoId);
                }
                
                const messageRef = convoRef.collection('messages').doc();
                const messageWithConvoId = { ...broadcastMessage, conversationId: convoId };
                
                batch.set(messageRef, messageWithConvoId);
                batch.update(convoRef, { lastMessage: messageWithConvoId });
            }
    
            await batch.commit();
        } catch (error) {
            console.error("Error sending broadcast message:", error);
        }
    };
    
    const giveCluesToUser = async (uid: string, amount: number) => {
        if (!uid || amount <= 0) return;
        try {
            await db.collection('users').doc(uid).update({
                clues: increment(amount)
            });
        } catch (error) {
            console.error(`Failed to give ${amount} clues to user ${uid}:`, error);
            throw error;
        }
    };
    
    const giveAICreditsToUser = async (uid: string, amount: number) => {
        if (!uid || amount <= 0) return;
        try {
            await db.collection('users').doc(uid).update({
                aiCredits: increment(amount)
            });
        } catch (error) {
            console.error(`Failed to give ${amount} AI credits to user ${uid}:`, error);
            throw error;
        }
    };
    
    const toggleBusinessStatus = async (uid: string, isBusiness: boolean) => {
        try {
            await db.collection('users').doc(uid).update({ isBusiness });
        } catch (error) {
            console.error("Failed to toggle business status:", error);
            throw error;
        }
    };

    const compensateUser = async (username: string, points: number, aiCredits: number, clues: number, message: string) => {
        try {
            const usernameDoc = await db.collection('usernames').doc(username.toLowerCase()).get();
            if (!usernameDoc.exists) {
                throw new Error(`User with username ${username} not found.`);
            }
            const uid = usernameDoc.data()?.uid;
            if (!uid) {
                throw new Error(`UID not found for username ${username}.`);
            }

            const updates: any = {};
            if (points > 0) updates.score = increment(points);
            if (aiCredits > 0) updates.aiCredits = increment(aiCredits);
            if (clues > 0) updates.clues = increment(clues);

            if (Object.keys(updates).length > 0) {
                await db.collection('users').doc(uid).update(updates);
            }

            if (message) {
                await addNotification(uid, {
                    type: 'system',
                    text: message,
                });
            }
        } catch (error) {
            console.error("Failed to compensate user:", error);
            throw error;
        }
    };
    
    const value: AdminContextType = {
        notifications,
        sentRequests,
        addNotification,
        sendFriendRequest,
        sendDirectNotification,
        reportedUsers,
        aiReports,
        submitAIReport,
        reportUser,
        dismissUserReport,
        resolveAIReport,
        refreshNotifications,
        banUser,
        unbanUser,
        deleteUser,
        banAndWipeUser,
        resetLeaderboard,
        sendBroadcastMessage,
        markAllNotificationsAsRead,
        markSingleNotificationAsRead,
        removeNotification,
        giveCluesToUser,
        giveAICreditsToUser,
        toggleBusinessStatus,
        reportGroup,
        compensateUser,
    };

    return (
        <AdminContext.Provider value={value}>
            {children}
        </AdminContext.Provider>
    );
};
